import React, { useState, useEffect, useRef } from "react";
import "./styles/Events.css";
import CustomCalendar from "../components/CustomCalendar";
import CustomUpcoming from "../components/CustomUpcoming";
import SearchIcon from "@mui/icons-material/Search";
import { useModal } from "../contexts/ModalContext";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { eventOccursOn } from "../utils/datetime";
import TuneIcon from "@mui/icons-material/Tune";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { types } from "../constants/eventVars";
import { getExerciseTypes } from "../api/UserAPI";

import runIcon from "../assets/movements/run.png";
import trailIcon from "../assets/movements/trail.png";
import walkIcon from "../assets/movements/walk.png";
import hikeIcon from "../assets/movements/hike.png";
import cycleIcon from "../assets/movements/cycle.png";
import swimIcon from "../assets/movements/swim.png";
import yogaIcon from "../assets/movements/yoga.png";
import weightTrainingIcon from "../assets/movements/weight_training.png";
import martialArtsIcon from "../assets/movements/martial_arts.png";
import golfIcon from "../assets/movements/golf.png";
import functionalTrainingIcon from "../assets/movements/functional_training.png";
import surfingIcon from "../assets/movements/surfing.png";
import hiitIcon from "../assets/movements/hiit.png";
import toucheesIcon from "../assets/movements/touchees.png";
import allIcon from "../assets/movements/all.png";

const defaultExerciseTypes = types.map((id) => ({
  id,
  name: id.charAt(0).toUpperCase() + id.slice(1).replace(/_/g, " "),
}));

const movementIconMap = {
  run: runIcon,
  trail: trailIcon,
  walk: walkIcon,
  hike: hikeIcon,
  cycle: cycleIcon,
  swim: swimIcon,
  yoga: yogaIcon,
  weight_training: weightTrainingIcon,
  martial_arts: martialArtsIcon,
  golf: golfIcon,
  functional_training: functionalTrainingIcon,
  surfing: surfingIcon,
  hiit: hiitIcon,
  touchees: toucheesIcon,
};

const Events = ({ eventSectionRef, eventData }) => {
  const { openModal } = useModal();
  const [searchText, setsearchText] = useState();
  const [filtersSelected, setfiltersSelected] = useState([...types, "other"]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth();
  const dayOfMonth = date.getDate();

  const [selectedMonth, setselectedMonth] = useState(month);

  const [calendarCompiledEvents, setcalendarCompiledEvents] = useState({});
  const [upcomingCompiledEvents, setupcomingCompiledEvents] = useState({});
  const [exerciseTypes, setExerciseTypes] = useState(defaultExerciseTypes);
  // Intersect Observer ( Checks if component is in view )
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  useEffect(() => {
    compileEventsInCalendar(month); //Calendar (PC only)
    compileEventsInUpcoming(month); // Upcoming (PC + Mobile)
  }, [eventData]);

  useEffect(() => {
    getExerciseTypes().then(res => {
      if (res.success && res.exerciseTypes.length > 0) {
        setExerciseTypes(res.exerciseTypes);
        setfiltersSelected(res.exerciseTypes.map(et => et.id).concat(['other']));
      }
      // else: keep the default exercise types so filters remain visible
      // (e.g. Firestore rules deny the read for signed-out users)
    });
  }, []);

  function eventsOnDate(lookupDate) {
    return eventData.filter((eventObj) => {
      try {
        return eventOccursOn(eventObj, lookupDate);
      } catch (err) {
        console.error("Error filtering event:", eventObj, err);
        return false; // Fail gracefully
      }
    });
  }

  function compileMonth(monthToCompile, fromDay = 1) {
    const lastDayOfMonth = new Date(year, monthToCompile + 1, 0).getDate();
    const compiledEvents = {};
    for (let day = fromDay; day <= lastDayOfMonth; day++) {
      const matches = eventsOnDate(new Date(year, monthToCompile, day));
      if (matches.length > 0) {
        //only add if there are events/meets for that day
        compiledEvents[day] = matches;
      }
    }
    return compiledEvents;
  }

  function compileEventsInCalendar(monthToCompile) {
    setcalendarCompiledEvents(compileMonth(monthToCompile));
  }

  function compileEventsInUpcoming(monthToCompile) {
    const compiledEventsThisMonth = compileMonth(monthToCompile, dayOfMonth);
    const compiledEventsNextMonth = compileMonth(monthToCompile + 1);
    setupcomingCompiledEvents({ compiledEventsThisMonth, compiledEventsNextMonth })
  }

  function handleMonthChange(operation) {
    const init = false
    if (operation === 'forward') {
      if (selectedMonth + 1 > 11) return
      compileEventsInCalendar(selectedMonth + 1, init)
      setselectedMonth(prev => prev + 1)
    } else {
      if (selectedMonth - 1 < 0) return

      compileEventsInCalendar(selectedMonth - 1, init)
      setselectedMonth(prev => prev - 1)

    }
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="events-section" ref={eventSectionRef}>
      <motion.div
        ref={ref}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={inView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="events-container"
      >
        <FilterBar
          filtersSelected={filtersSelected}
          setfiltersSelected={setfiltersSelected}
          exerciseTypes={exerciseTypes}
        />
        <div className="content">
          {!isMobile && (
            <CustomCalendar
              handleMonthChange={handleMonthChange}
              filtersSelected={filtersSelected}
              year={year}
              displayMonth={selectedMonth}
              currentMonth={month}
              dayOfMonth={dayOfMonth}
              eventsData={calendarCompiledEvents}
              openModal={openModal}
            />
          )}
          <CustomUpcoming
            filtersSelected={['all',...filtersSelected]}
            dayOfMonth={dayOfMonth}
            currentMonth={month}
            year={year}
            eventsData={upcomingCompiledEvents}
            openModal={openModal}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default Events;

const FilterBar = ({ filtersSelected, setfiltersSelected, exerciseTypes }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  useEffect(() => {
    updateScrollState();
  }, [exerciseTypes]);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir === "left" ? -220 : 220, behavior: "smooth" });
  };

  return (
    <div className="filter-bar-container">
      <div className="filter-bar">
        <TuneIcon sx={{ color: "white", flexShrink: 0 }} />
        <div className="filter-scroll-wrapper">
          {canScrollLeft && (
            <button className="filter-scroll-btn left" onClick={() => scroll("left")}>
              <ChevronLeftIcon sx={{ fontSize: "18px" }} />
            </button>
          )}
          <div className="filter-bar-content" ref={scrollRef} onScroll={updateScrollState}>
            {exerciseTypes.map((et) => (
              <div
                key={et.id}
                className={`filter-button${filtersSelected.includes(et.id) ? " selected" : ""}`}
                onClick={() => {
                  if (filtersSelected.includes(et.id)) {
                    setfiltersSelected(filtersSelected.filter(item => item !== et.id));
                  } else {
                    setfiltersSelected([...filtersSelected, et.id]);
                  }
                }}
              >
                <img
                  src={movementIconMap[et.id] || allIcon}
                  alt={et.name || et.id}
                  className="filter-btn-icon"
                />
                <p>{et.name || et.id}</p>
              </div>
            ))}
          </div>
          {canScrollRight && (
            <button className="filter-scroll-btn right" onClick={() => scroll("right")}>
              <ChevronRightIcon sx={{ fontSize: "18px" }} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Custom Search Bar for this site
const SearchBar = ({ searchText, setSearchText }) => {
  const [focused, setFocused] = useState(false); // State to track focus
  const handleFocus = () => {
    if (searchText === "Search") {
      setSearchText("");
    }
    setFocused(true); // Set the focused state to true
  };
  const handleBlur = () => {
    setFocused(false); // Set the focused state to false when the input loses focus
  };
  const handleChange = (e) => {
    setSearchText(e.target.value);
  };
  return (
    <div className={`search-bar-container ${focused ? "focused" : ""}`}>
      <SearchIcon className="search-icon" />
      <input
        name="searchbar"
        className={`search-bar ${focused ? "focused" : ""}`}
        placeholder="Search all events"
        value={searchText}
        onFocus={handleFocus}
        onBlur={handleBlur} // Add onBlur to handle loss of focus
        onChange={handleChange}
      ></input>
      <button className="search-button">
        <p>Search</p>
      </button>
    </div>
  );
};
